import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router';
import {useState, useEffect, useRef} from 'react';
import {
  checkOrganizerEditAccess,
  getOrganizerById,
  getDashboardOrganizerEvents,
} from '@/server/organizer';
import {archiveEvent, restoreEvent} from '@/server/event';
import {
  getOrganizerAdmins,
  inviteToOrganizer,
  revokeInvite,
  resendInvite,
  removeAdmin,
} from '@/server/organizerInvite';
import {getConnectAccountStatus} from '@/server/stripe';
import {
  addOrganizerMember,
  getOrganizerMembers,
  removeOrganizerMember,
  searchAddableMembers,
} from '@/server/organizerMember';
import type {
  MemberSearchResult,
  OrganizerMemberRow,
} from '@/apiTypes/organizerMember';
import {AddMemberCombobox} from '@/components/dashboard/AddMemberCombobox';
import {getProfileName} from '@/utils/getProfileName';
import {
  AlertDialog,
  AlertDialogTrigger,
  AlertDialogPopup,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogClose,
} from '@/components/ui/alert-dialog';
import {Card, CardContent} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Navbar} from '@/components/ui/navbar';
import {Spinner} from '@/components/ui/spinner';
import {toastManager} from '@/components/ui/toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import {EditOrganizerSection} from '@/components/adminEditOrganizer/adminEditOrganizer';
import {OrganizerClaimSection} from '@/components/adminEditOrganizer/organizerClaimSection';
import {MembersSection} from '@/components/dashboard/MembersSection';
import {Input} from '@/components/ui/input';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ArrowLeft,
  LayoutDashboard,
  CalendarDays,
  Pencil,
  Menu,
  X,
  ChevronLeft,
  ChevronRight,
  Users,
  Eye,
  CheckCircle2,
  AlertCircle,
  CreditCard,
  Clock,
  Mail,
  Trash2,
  RefreshCw,
  Loader2,
  UserPlus,
  Archive,
  ArchiveRestore,
  ArrowRight,
} from 'lucide-react';
import {EventTypeBadge} from '@/components/eventTypeBadge/eventTypeBadge';

type OrganizerData = Awaited<ReturnType<typeof getOrganizerById>>;

type SortField = 'name' | 'eventType' | 'startDate' | 'totalParticipants';
type SortOrder = 'asc' | 'desc';
type EventTab = 'upcoming' | 'past' | 'archived';

/**
 * Resolves the default sort order for a given tab when the URL does not
 * carry an explicit `sortOrder`. Upcoming defaults to soonest-first; Past
 * and Archived default to most-recent-first. Accepts a wider `string` to
 * play well with TanStack Router's widened search/loader-deps inference.
 */
function defaultSortOrderForTab(tab: string): SortOrder {
  return tab === 'upcoming' ? 'asc' : 'desc';
}

export const Route = createFileRoute('/admin/dashboard/organizer/$organizerId')(
  {
    component: OrganizerDashboardPage,
    validateSearch: (search: Record<string, unknown>) => {
      const sortBy = String(search.sortBy || 'startDate');
      const validSortBy =
        sortBy === 'name' ||
        sortBy === 'eventType' ||
        sortBy === 'startDate' ||
        sortBy === 'totalParticipants'
          ? sortBy
          : 'startDate';
      const tab =
        search.tab === 'past' || search.tab === 'archived'
          ? search.tab
          : 'upcoming';
      const sortOrder =
        search.sortOrder === 'asc' || search.sortOrder === 'desc'
          ? search.sortOrder
          : undefined;
      return {
        page: Number(search.page) || 1,
        sortBy: validSortBy,
        sortOrder,
        tab,
      };
    },
    beforeLoad: async ({context, location, params}) => {
      if (!context.profile) {
        throw redirect({
          to: '/login',
          search: {redirect: location.href},
        });
      }

      const {hasAccess} = await checkOrganizerEditAccess({
        data: params.organizerId,
      });
      if (!hasAccess) {
        throw redirect({
          to: '/organizer/$organizerId',
          params: {organizerId: params.organizerId},
          search: {unauthorized: true},
        });
      }

      return {isSuperAdmin: context.profile.level === 'superadmin'};
    },
    loaderDeps: ({search: {page, sortBy, sortOrder, tab}}) => ({
      page,
      sortBy,
      sortOrder,
      tab,
    }),
    loader: async ({params, deps, context}) => {
      const resolvedSortOrder =
        deps.sortOrder ?? defaultSortOrderForTab(deps.tab);
      const [organizer, eventsData, adminsData, membersData] =
        await Promise.all([
          getOrganizerById({data: params.organizerId}),
          getDashboardOrganizerEvents({
            data: {
              organizerId: params.organizerId,
              page: deps.page,
              sortBy: deps.sortBy,
              sortOrder: resolvedSortOrder,
              timeFilter: deps.tab,
            },
          }),
          getOrganizerAdmins({data: params.organizerId}),
          getOrganizerMembers({
            data: {
              organizerId: params.organizerId,
              page: 1,
              pageSize: 25,
              sortBy: 'joinedAt',
              sortOrder: 'desc',
            },
          }),
        ]);

      // Fetch Stripe account status when a Connect account exists
      let stripeStatus = null;
      if (organizer.stripeConnectId) {
        try {
          stripeStatus = await getConnectAccountStatus({
            data: {organizerId: params.organizerId},
          });
        } catch {
          // Non-fatal: dashboard still works without Stripe status
        }
      }

      return {
        organizer,
        eventsData,
        adminsData,
        membersData,
        stripeStatus,
        isSuperAdmin: context.isSuperAdmin,
      };
    },
    errorComponent: ({error}) => {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <Card className="max-w-md mx-4">
            <CardContent className="p-6 text-center">
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Organization Not Found
              </h1>
              <p className="text-gray-600 mb-4">
                {error?.message ||
                  "The organization you're looking for doesn't exist."}
              </p>
              <Link to="/search">
                <Button>Browse Events</Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      );
    },
  }
);

type SidebarSection = 'overview' | 'events' | 'members' | 'admins' | 'edit';

const sidebarItems: {
  id: SidebarSection;
  label: string;
  icon: typeof LayoutDashboard;
}[] = [
  {id: 'overview', label: 'Overview', icon: LayoutDashboard},
  {id: 'events', label: 'Events', icon: CalendarDays},
  {id: 'members', label: 'Members', icon: Users},
  {id: 'admins', label: 'Admins', icon: Users},
  {id: 'edit', label: 'Edit Organization', icon: Pencil},
];

/** Sidebar navigation links shared between desktop and mobile drawer. */
function SidebarNav({
  activeSection,
  onSelect,
  organizerId,
}: {
  activeSection: SidebarSection;
  onSelect?: (section: SidebarSection) => void;
  organizerId: string;
}) {
  return (
    <nav className="space-y-1">
      {sidebarItems.map((item) => {
        const Icon = item.icon;
        const isActive = activeSection === item.id;
        return (
          <button
            key={item.id}
            type="button"
            onClick={() => onSelect?.(item.id)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
              isActive
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            <Icon className="w-4 h-4" />
            {item.label}
          </button>
        );
      })}
      <div className="border-t border-gray-200 my-3" />
      <Link
        to="/organizer/$organizerId"
        params={{organizerId}}
        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        View Organization
      </Link>
    </nav>
  );
}

type StripeStatus = Awaited<ReturnType<typeof getConnectAccountStatus>> | null;

const POLL_INTERVAL_MS = 5000;

/**
 * Blue "Processing" card that polls Stripe every few seconds and
 * invalidates the route data once capabilities become active.
 */
function StripeProcessingCard({organizerId}: {organizerId: string}) {
  const router = useRouter();

  useEffect(() => {
    let cancelled = false;

    async function poll() {
      if (cancelled) return;
      try {
        const status = await getConnectAccountStatus({
          data: {organizerId},
        });
        if (status.readyToProcessPayments) {
          router.invalidate();
          return;
        }
      } catch {
        // Non-fatal — keep polling
      }
      if (!cancelled) {
        timeout = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    let timeout = setTimeout(poll, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
  }, [organizerId, router]);

  return (
    <Card className="border border-blue-200 bg-blue-50">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <Spinner size="sm" />
          <div>
            <p className="text-sm font-medium text-blue-900">Processing</p>
            <p className="text-xs text-blue-700 mt-0.5">
              Stripe is reviewing your account. This usually takes a few
              minutes.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Stripe Connect card that shows the current onboarding state and
 * provides navigation to begin or complete Stripe Connect setup.
 */
function StripeConnectCard({
  organizer,
  stripeStatus,
}: {
  organizer: OrganizerData;
  stripeStatus: StripeStatus;
}) {
  // Fully active: card_payments capability is active
  if (organizer.canAcceptPayments) {
    return (
      <Card className="border border-green-200 bg-green-50">
        <CardContent className="p-5">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-900">
                Payments Active
              </p>
              <p className="text-xs text-green-700 mt-0.5">
                Your organization can accept payments through Stripe
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Onboarding complete, waiting for Stripe to activate capabilities
  if (organizer.stripeConnectId && stripeStatus?.onboardingComplete) {
    return (
      <StripeProcessingCard organizerId={organizer.id} />
    );
  }

  // Has Stripe account but onboarding incomplete
  if (organizer.stripeConnectId) {
    return (
      <Card className="border border-amber-200 bg-amber-50">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Payment Onboarding Incomplete
                </p>
                <p className="text-xs text-amber-700 mt-0.5">
                  Complete your setup to start accepting payments
                </p>
              </div>
            </div>
            <Link
              to="/admin/connect/$organizerId"
              params={{organizerId: organizer.id}}
            >
              <Button size="sm">Complete Setup</Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    );
  }

  // No Stripe account yet — link to connect page which creates the account
  return (
    <Card className="border border-gray-200">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <CreditCard className="w-5 h-5 text-gray-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-gray-900">
                Accept Payments
              </p>
              <p className="text-xs text-gray-500 mt-0.5">
                Setup your account to collect payments for events
              </p>
            </div>
          </div>
          <Link
            to="/admin/connect/$organizerId"
            params={{organizerId: organizer.id}}
          >
            <Button size="sm">Setup Payments</Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

/** Placeholder card shown when the stripe feature flag is disabled. */
function PaymentsComingSoonCard() {
  return (
    <Card className="border border-gray-200">
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-gray-400" />
          <div>
            <p className="text-sm font-medium text-gray-900">
              Payments coming soon.
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              Payment processing will be available in a future update.
            </p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/** Overview section showing organization stats and Stripe Connect status. */
function OverviewSection({
  organizer,
  stripeStatus,
  onViewEvents,
}: {
  organizer: OrganizerData;
  stripeStatus: StripeStatus;
  onViewEvents: () => void;
}) {
  const {featureFlags} = Route.useRouteContext();
  const stripeEnabled = featureFlags.stripe_enabled;

  return (
    <div>
      <h2 className="text-lg font-semibold text-neutral-900 mb-4">Overview</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <button type="button" onClick={onViewEvents} className="text-left">
          <Card className="border border-neutral-200 hover:border-neutral-400 transition-colors cursor-pointer">
            <CardContent className="flex justify-between items-center p-5">
              <div className="flex flex-col">
                <p className="text-sm text-gray-500">Total Events</p>
                <p className="text-2xl font-bold text-gray-900">
                  {organizer.totalEvents}
                </p>
              </div>
              <ArrowRight className="w-4 h-4 text-gray-400" />
            </CardContent>
          </Card>
        </button>
        <Card className="border border-neutral-200">
          <CardContent className="p-5">
            <p className="text-sm text-neutral-500 mb-1">Total Participants</p>
            <p className="text-2xl font-bold text-neutral-900">
              {organizer.totalParticipants}
            </p>
          </CardContent>
        </Card>
      </div>

      {organizer.description && (
        <Card className="border border-neutral-200 mt-4">
          <CardContent className="p-5">
            <p className="text-sm text-neutral-500 mb-1">Description</p>
            <p className="text-sm text-neutral-700">{organizer.description}</p>
          </CardContent>
        </Card>
      )}

      <div className="mt-4">
        {stripeEnabled ? (
          <StripeConnectCard
            organizer={organizer}
            stripeStatus={stripeStatus}
          />
        ) : (
          <PaymentsComingSoonCard />
        )}
      </div>

    </div>
  );
}

/** Formats a date for display in the events table. */
function formatEventDate(date: Date | string | null): string {
  if (!date) return '—';
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

/** Events table listing all organization events with pagination. */
function EventsSection({
  eventsData,
  organizerId,
  sortBy,
  sortOrder,
  tab,
}: {
  eventsData: Awaited<ReturnType<typeof getDashboardOrganizerEvents>>;
  organizerId: string;
  sortBy: SortField;
  sortOrder: SortOrder | undefined;
  tab: EventTab;
}) {
  const {events, pagination} = eventsData;
  const {page, totalPages, totalCount} = pagination;
  const router = useRouter();
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const resolvedSortOrder = sortOrder ?? defaultSortOrderForTab(tab);

  /** Archives or restores an event and refreshes the list. */
  const handleArchiveToggle = async (eventId: string, restore: boolean) => {
    setLoadingId(eventId);
    try {
      if (restore) {
        await restoreEvent({data: eventId});
        toastManager.add({title: 'Event restored', type: 'success'});
      } else {
        await archiveEvent({data: eventId});
        toastManager.add({title: 'Event archived', type: 'success'});
      }
      router.invalidate();
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Something went wrong',
        type: 'error',
      });
    } finally {
      setLoadingId(null);
    }
  };

  const isArchived = tab === 'archived';
  const emptyStateCopy: Record<EventTab, string> = {
    upcoming: 'No upcoming events.',
    past: 'No past events.',
    archived: 'No archived events.',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-neutral-900 font-sans">
            Events
          </h2>
          <p className="text-sm text-neutral-500">
            {totalCount} {totalCount === 1 ? 'event' : 'events'}
          </p>
        </div>
        <Link to="/create-event">
          <Button size="sm">Create Event</Button>
        </Link>
      </div>

      {/* Upcoming / Past / Archived tabs */}
      <div className="flex gap-1 mb-4 border-b border-neutral-200">
        {(
          [
            {id: 'upcoming', label: 'Upcoming'},
            {id: 'past', label: 'Past'},
            {id: 'archived', label: 'Archived'},
          ] satisfies Array<{id: EventTab; label: string}>
        ).map(({id, label}) => (
          <Link
            key={id}
            to="/admin/dashboard/organizer/$organizerId"
            params={{organizerId}}
            search={{page: 1, sortBy, sortOrder, tab: id}}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
              tab === id
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-neutral-500 hover:text-neutral-700'
            }`}
          >
            {label}
          </Link>
        ))}
      </div>

      {totalCount === 0 ? (
        <Card className="border border-neutral-200">
          <CardContent className="py-12 text-center">
            <CalendarDays className="w-10 h-10 text-neutral-300 mx-auto mb-3" />
            <p className="text-neutral-500 text-sm mb-3">
              {emptyStateCopy[tab]}
            </p>
            {tab === 'upcoming' && (
              <Link to="/create-event">
                <Button size="sm">Create Your First Event</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card className="border border-neutral-200">
          <Table>
            <TableHeader>
              <TableRow>
                {(
                  [
                    {field: 'name', label: 'Event'},
                    {field: 'eventType', label: 'Type'},
                    {field: 'startDate', label: 'Date'},
                    {field: 'totalParticipants', label: 'Participants'},
                  ] satisfies Array<{field: SortField; label: string}>
                ).map(({field, label}) => {
                  const isActive = sortBy === field;
                  const nextOrder =
                    isActive && resolvedSortOrder === 'asc' ? 'desc' : 'asc';
                  const SortIcon = isActive
                    ? resolvedSortOrder === 'asc'
                      ? ArrowUp
                      : ArrowDown
                    : ArrowUpDown;
                  return (
                    <TableHead key={field}>
                      <Link
                        to="/admin/dashboard/organizer/$organizerId"
                        params={{organizerId}}
                        search={{
                          page: 1,
                          sortBy: field,
                          sortOrder: nextOrder,
                          tab,
                        }}
                        className="flex items-center gap-1 cursor-pointer select-none hover:text-neutral-900"
                      >
                        {label}
                        <SortIcon
                          className={`w-3.5 h-3.5 ${isActive ? 'text-neutral-900' : 'text-neutral-400'}`}
                        />
                      </Link>
                    </TableHead>
                  );
                })}
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id}>
                  <TableCell>
                    <span className="font-medium text-neutral-900">
                      {event.name}
                    </span>
                    {event.location && (
                      <p className="text-xs text-neutral-500 mt-0.5">
                        {event.location}
                      </p>
                    )}
                  </TableCell>
                  <TableCell>
                    <EventTypeBadge
                      eventType={event.eventType}
                      className="text-center rounded-full text-xs"
                    />
                  </TableCell>
                  <TableCell className="text-neutral-600">
                    {formatEventDate(event.startDate)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 text-neutral-600">
                      <Users className="w-3.5 h-3.5" />
                      {event.totalParticipants}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Link to="/event/$eventId" params={{eventId: event.id}}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Link
                        to="/admin/dashboard/event/$eventId"
                        params={{eventId: event.id}}
                      >
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <Pencil className="w-4 h-4" />
                        </Button>
                      </Link>
                      <AlertDialog>
                        <AlertDialogTrigger
                          render={
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              disabled={loadingId === event.id}
                              title={
                                isArchived ? 'Restore event' : 'Archive event'
                              }
                            >
                              {loadingId === event.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : isArchived ? (
                                <ArchiveRestore className="w-4 h-4" />
                              ) : (
                                <Archive className="w-4 h-4" />
                              )}
                            </Button>
                          }
                        />
                        <AlertDialogPopup>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {isArchived
                                ? 'Restore this event?'
                                : 'Archive this event?'}
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                              {isArchived
                                ? 'This event will be visible again in public search and the map.'
                                : 'This event will be hidden from public search and the map. You can restore it later from the Archived tab.'}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogClose
                              render={<Button variant="outline">Cancel</Button>}
                            />
                            <AlertDialogClose
                              render={
                                <Button
                                  onClick={() =>
                                    handleArchiveToggle(event.id, isArchived)
                                  }
                                >
                                  {isArchived
                                    ? 'Restore Event'
                                    : 'Archive Event'}
                                </Button>
                              }
                            />
                          </AlertDialogFooter>
                        </AlertDialogPopup>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-200">
              <p className="text-sm text-neutral-500">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  asChild={page > 1}
                >
                  {page > 1 ? (
                    <Link
                      to="/admin/dashboard/organizer/$organizerId"
                      params={{organizerId}}
                      search={{page: page - 1, sortBy, sortOrder, tab}}
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </Link>
                  ) : (
                    <>
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Previous
                    </>
                  )}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  asChild={page < totalPages}
                >
                  {page < totalPages ? (
                    <Link
                      to="/admin/dashboard/organizer/$organizerId"
                      params={{organizerId}}
                      search={{page: page + 1, sortBy, sortOrder, tab}}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Link>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}

type AdminsData = Awaited<ReturnType<typeof getOrganizerAdmins>>;

/** Admins and invites management section. */
function AdminsSection({
  adminsData: initialData,
  organizerId,
}: {
  adminsData: AdminsData;
  organizerId: string;
}) {
  const [data, setData] = useState(initialData);
  const [inviteEmail, setInviteEmail] = useState('');
  const [isInviting, setIsInviting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);

  /** Refetches member and invite lists from the server. */
  const refreshData = async () => {
    const fresh = await getOrganizerAdmins({data: organizerId});
    setData(fresh);
  };

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setIsInviting(true);

    try {
      await inviteToOrganizer({
        data: {organizerId, email: inviteEmail},
      });
      setInviteEmail('');
      setSuccessMessage(`Invite sent to ${inviteEmail}`);
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send invite');
    } finally {
      setIsInviting(false);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    setLoadingAction(inviteId);
    try {
      await revokeInvite({data: {inviteId, organizerId}});
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke invite');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleResend = async (inviteId: string) => {
    setLoadingAction(inviteId);
    setSuccessMessage(null);
    try {
      await resendInvite({data: {inviteId, organizerId}});
      setSuccessMessage('Invite resent successfully');
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend invite');
    } finally {
      setLoadingAction(null);
    }
  };

  const handleRemoveAdmin = async (adminId: string) => {
    setLoadingAction(adminId);
    try {
      await removeAdmin({data: {adminId, organizerId}});
      await refreshData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove admin');
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-4">Admins</h2>

      {/* Invite form */}
      <Card className="border border-gray-200 mb-6">
        <CardContent className="p-5">
          <h3 className="text-sm font-medium text-gray-900 mb-3">
            Invite a new admin
          </h3>
          <form onSubmit={handleInvite} className="flex gap-3">
            <Input
              type="email"
              placeholder="email@example.com"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              required
              className="flex-1"
            />
            <Button type="submit" size="sm" disabled={isInviting}>
              {isInviting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-1" />
                  Invite
                </>
              )}
            </Button>
          </form>
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          {successMessage && (
            <p className="text-sm text-green-600 mt-2">{successMessage}</p>
          )}
        </CardContent>
      </Card>

      {/* Current admins */}
      <Card className="border border-gray-200 mb-6">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Admin</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.admins.map((admin) => (
              <TableRow key={admin.id}>
                <TableCell>
                  <div className="flex items-center gap-3">
                    {admin.profile.avatarUrl ? (
                      <img
                        src={admin.profile.avatarUrl}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                        <span className="text-xs font-medium text-gray-500">
                          {(
                            admin.profile.firstName ||
                            admin.profile.displayName ||
                            admin.profile.email
                          )
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                      </div>
                    )}
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {admin.profile.firstName && admin.profile.lastName
                          ? `${admin.profile.firstName} ${admin.profile.lastName}`
                          : admin.profile.displayName || admin.profile.email}
                      </p>
                      <p className="text-xs text-gray-500">
                        {admin.profile.email}
                      </p>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  {data.admins.length > 1 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-gray-400 hover:text-red-600"
                      onClick={() => handleRemoveAdmin(admin.id)}
                      disabled={loadingAction === admin.id}
                    >
                      {loadingAction === admin.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Pending invites */}
      {data.invites.length > 0 && (
        <>
          <h3 className="text-sm font-semibold text-gray-900 mb-3">
            Pending Invites
          </h3>
          <Card className="border border-gray-200">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.invites.map((invite) => (
                  <TableRow key={invite.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-900">
                          {invite.email}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {invite.isExpired ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700">
                          <Clock className="w-3 h-3" />
                          Expired
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                          Pending
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-blue-600"
                          onClick={() => handleResend(invite.id)}
                          disabled={loadingAction === invite.id}
                          title="Resend invite"
                        >
                          {loadingAction === invite.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RefreshCw className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-gray-400 hover:text-red-600"
                          onClick={() => handleRevoke(invite.id)}
                          disabled={loadingAction === invite.id}
                          title="Revoke invite"
                        >
                          {loadingAction === invite.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </>
      )}
    </div>
  );
}

type MembersData = Awaited<ReturnType<typeof getOrganizerMembers>>;

/**
 * Page-level container for the Members tab. Owns the search input,
 * debounced search query, sort, page, and current data. Calls the
 * `getOrganizerMembers` server fn on state change and passes everything
 * to the pure-render `<MembersSection>`.
 *
 * Lives in the route file (not in `components/`) so server-fn calls and
 * state management stay at the page level per the project convention.
 */
function MembersContainer({
  initialData,
  organizerId,
}: {
  initialData: MembersData;
  organizerId: string;
}) {
  const [data, setData] = useState<MembersData>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'joinedAt'>('joinedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [refreshKey, setRefreshKey] = useState(0);

  // Add-member panel state lives here so the AddMemberCombobox can stay a
  // pure render atom — server fns and refetch wiring stay at the page level.
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [addQuery, setAddQuery] = useState('');
  const [debouncedAddQuery, setDebouncedAddQuery] = useState('');
  const [addResults, setAddResults] = useState<MemberSearchResult[]>([]);
  const [isSearchingAddable, setIsSearchingAddable] = useState(false);
  const [hasSearchError, setHasSearchError] = useState(false);
  const [addingProfileId, setAddingProfileId] = useState<string | null>(null);
  const [removingProfileId, setRemovingProfileId] = useState<string | null>(
    null
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedAddQuery(addQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [addQuery]);

  useEffect(() => {
    if (debouncedAddQuery.length < 2) {
      setAddResults([]);
      setIsSearchingAddable(false);
      setHasSearchError(false);
      return;
    }
    let cancelled = false;
    setIsSearchingAddable(true);
    setHasSearchError(false);
    searchAddableMembers({
      data: {organizerId, query: debouncedAddQuery},
    })
      .then((results) => {
        if (!cancelled) setAddResults(results);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('searchAddableMembers failed', {
          organizerId,
          query: debouncedAddQuery,
          error: err,
        });
        setAddResults([]);
        setHasSearchError(true);
      })
      .finally(() => {
        if (!cancelled) setIsSearchingAddable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizerId, debouncedAddQuery]);

  const isFirstRun = useRef(true);
  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getOrganizerMembers({
      data: {
        organizerId,
        page,
        pageSize: 25,
        sortBy,
        sortOrder,
        search: debouncedSearch || undefined,
      },
    })
      .then((fresh) => {
        if (!cancelled) setData(fresh);
      })
      .catch((err) => {
        if (!cancelled) {
          toastManager.add({
            title:
              err instanceof Error ? err.message : 'Failed to load members',
            type: 'error',
          });
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [organizerId, page, sortBy, sortOrder, debouncedSearch, refreshKey]);

  /** Reset to page 1 whenever the search input changes. */
  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPage(1);
  };

  /** Toggle sort: same field flips order, new field starts ascending. */
  const handleToggleSort = (field: 'name' | 'joinedAt') => {
    if (field === sortBy) {
      setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  /**
   * Adds the picked profile to the org. On success, clears the typeahead
   * input but leaves the panel open so an admin can bulk-add several
   * people in a row, and bumps refreshKey to refetch the table.
   */
  const handleSelectAddable = async (profile: MemberSearchResult) => {
    setAddingProfileId(profile.id);
    try {
      await addOrganizerMember({
        data: {organizerId, profileId: profile.id},
      });
      const fullName = getProfileName(profile);
      toastManager.add({
        title: `Added ${fullName} to members.`,
        type: 'success',
      });
      setAddQuery('');
      setDebouncedAddQuery('');
      setAddResults([]);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to add member',
        type: 'error',
      });
    } finally {
      setAddingProfileId(null);
    }
  };

  /** Collapses the add panel and clears any in-flight typeahead state. */
  const handleCloseAddPanel = () => {
    setIsAddPanelOpen(false);
    setAddQuery('');
    setDebouncedAddQuery('');
    setAddResults([]);
  };

  /**
   * Soft-deletes the row (sets leftAt = now() server-side) and refetches.
   * The confirmation dialog already gated this; if the underlying row was
   * removed in another tab between dialog-open and confirm, the server
   * fn throws "Profile is not a current member" and we surface it as a
   * toast.
   */
  const handleRemoveMember = async (member: OrganizerMemberRow) => {
    setRemovingProfileId(member.profile.id);
    try {
      await removeOrganizerMember({
        data: {organizerId, profileId: member.profile.id},
      });
      const fullName = getProfileName(member.profile, member.profile.email);
      toastManager.add({
        title: `Removed ${fullName} from members.`,
        type: 'success',
      });
      setRefreshKey((k) => k + 1);
    } catch (err) {
      toastManager.add({
        title: err instanceof Error ? err.message : 'Failed to remove member',
        type: 'error',
      });
    } finally {
      setRemovingProfileId(null);
    }
  };

  return (
    <MembersSection
      data={data}
      isLoading={isLoading}
      searchQuery={searchInput}
      sortBy={sortBy}
      sortOrder={sortOrder}
      isAddPanelOpen={isAddPanelOpen}
      addPanel={
        <AddMemberCombobox
          query={addQuery}
          results={addResults}
          isSearching={isSearchingAddable}
          hasSearchError={hasSearchError}
          addingProfileId={addingProfileId}
          onQueryChange={setAddQuery}
          onSelect={handleSelectAddable}
          onClose={handleCloseAddPanel}
        />
      }
      removingProfileId={removingProfileId}
      onSearchChange={handleSearchChange}
      onToggleSort={handleToggleSort}
      onPageChange={setPage}
      onOpenAddPanel={() => setIsAddPanelOpen(true)}
      onRemoveMember={handleRemoveMember}
    />
  );
}

function OrganizerDashboardPage() {
  const {
    organizer,
    eventsData,
    adminsData,
    membersData,
    stripeStatus,
    isSuperAdmin,
  } = Route.useLoaderData();
  const {sortBy, sortOrder, tab} = Route.useSearch();
  const [activeSection, setActiveSection] =
    useState<SidebarSection>('overview');
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      {/* Top bar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          {/* Mobile menu trigger */}
          <Drawer
            open={drawerOpen}
            onOpenChange={setDrawerOpen}
            direction="left"
          >
            <DrawerTrigger asChild>
              <button
                type="button"
                className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-gray-100"
              >
                <Menu className="w-5 h-5 text-gray-700" />
              </button>
            </DrawerTrigger>
            <DrawerContent>
              <DrawerHeader className="flex items-center justify-between">
                <DrawerTitle>Dashboard</DrawerTitle>
                <DrawerClose asChild>
                  <button
                    type="button"
                    className="p-1 rounded hover:bg-gray-100"
                  >
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                </DrawerClose>
              </DrawerHeader>
              <div className="px-4 pb-4">
                <SidebarNav
                  activeSection={activeSection}
                  onSelect={(section) => {
                    setActiveSection(section);
                    setDrawerOpen(false);
                  }}
                  organizerId={organizer.id}
                />
              </div>
            </DrawerContent>
          </Drawer>

          {organizer.logoUrl ? (
            <img
              src={organizer.logoUrl}
              alt={`${organizer.name} logo`}
              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
            />
          ) : (
            <div className="w-10 h-10 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-semibold text-gray-500">
                {organizer.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-lg font-semibold text-gray-900 truncate">
              {organizer.name}
            </h1>
            <p className="text-xs text-gray-500">Organization Dashboard</p>
          </div>
        </div>
      </div>

      {/* Main layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop sidebar */}
          <div className="hidden lg:block w-56 flex-shrink-0">
            <div className="sticky top-20">
              <SidebarNav
                activeSection={activeSection}
                onSelect={setActiveSection}
                organizerId={organizer.id}
              />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {activeSection === 'overview' && (
              <OverviewSection
                organizer={organizer}
                stripeStatus={stripeStatus}
                onViewEvents={() => setActiveSection('events')}
              />
            )}
            {activeSection === 'events' && (
              <EventsSection
                eventsData={eventsData}
                organizerId={organizer.id}
                sortBy={sortBy}
                sortOrder={sortOrder}
                tab={tab}
              />
            )}
            {activeSection === 'members' && (
              <MembersContainer
                initialData={membersData}
                organizerId={organizer.id}
              />
            )}
            {activeSection === 'admins' && (
              <AdminsSection
                adminsData={adminsData}
                organizerId={organizer.id}
              />
            )}
            {activeSection === 'edit' && (
              <div className="space-y-6">
                <EditOrganizerSection organizer={organizer} isSuperAdmin={isSuperAdmin} />
                {isSuperAdmin && (
                  <OrganizerClaimSection
                    organizerId={organizer.id}
                    totalAdmins={organizer.totalAdmins}
                  />
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
